import { Box, Card, CardContent, Container, Grid, LinearProgress, styled, Typography } from '@mui/material'
import { Icon } from 'components'
import { CheckmarkCircle } from 'components/checkmarkCircle'
import { theme } from 'config'
import { inject, observer } from 'mobx-react'
import { Course, Step } from 'models'
import React, { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { RoutingStore } from 'stores'
import { CourseRating } from './courseRating'

export interface IQuizValidationProps {
  step?:          Step,
  routingStore?:  RoutingStore,
  course?:        Course
  setRatingAdded: Dispatch<SetStateAction<boolean>>
  isColumn?: boolean
}

export const QuizResult = inject('routingStore')(
  observer((props: IQuizValidationProps): JSX.Element => {
    const { step, routingStore, course, setRatingAdded, isColumn } = props
    const { t } = useTranslation()
    const numCorrect: number = step?.quiz?.correct_answers
    const already_completed_before: boolean =
      step?.isCompleted && numCorrect === -1 // True if the quiz was already conmpleted in the past

    const buttonHeight = '86px'

    if (step?.quiz?.is_control_quiz){
      if(step?.quiz?.showResults){
        return (
          <Container
            className='quizResult'
            maxWidth='sm'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: `calc(100vh - ${buttonHeight} - ${theme.topbarHeight}*2)`,
            }}
          >
            <Card
              elevation={3}
              sx={{
                maxWidth: 480,
                mx: 'auto',
                p: 2,
                borderRadius: '6px',
              }}
            >
              <CardContent>
                <Box
                  gap={2}
                  display='flex'
                  justifyContent='center'
                  alignItems='center'
                  flexDirection='column'
                >
                  <Box
                    display='flex'
                    flexDirection='column'
                    alignItems='center'
                    gap={2}
                  >
                    <Typography
                      variant='h4'
                      sx={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {t('quiz_result')}:
                    </Typography>
                    <Typography
                      variant='h4'
                      sx={{
                        textAlign: 'center',
                        fontWeight: 'normal',
                      }}
                    >
                      {t('control_question_answers_feedback', {
                        numCorrect,
                        totalQuestions: step.quiz?.tasks.length,
                      })}{' '}
                      (
                      {Math.round((numCorrect / step.quiz?.tasks.length) * 100)}
                      %)
                    </Typography>
                  </Box>

                  <Box sx={{ width: '100%'}}>
                    <LinearProgress
                      variant='determinate'
                      value={(numCorrect / step.quiz?.tasks.length) * 100}
                      sx={{
                        height: 16,
                        borderRadius: 5,
                      }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Container>
        )
      } else {
        return null;
      }
    }

    if (isColumn) {
      return (
      <Container maxWidth='sm' sx={{ alignItems: 'center', pt: 4 }}>
        <Box
          gap={4}
          display='flex'
          justifyContent='center'
          alignItems='center'
          flexDirection='column'
        >
          <Typography sx={{ mb: '2rem', mt: '2rem' }} variant='h5'>
            {t('completed_quiz_result_feedback')}!
          </Typography>
          <Typography sx={{ mb: '2rem' }} variant='body1'>
            {t('completed_quiz_result_details_column')}
          </Typography>
        </Box>
      </Container>
   )}

   if (!already_completed_before && !step.quiz?.showResults) {
     if (!step.quiz?.isPassed) return null
     return (
       <Container maxWidth='sm' sx={{ alignItems: 'center', pt: 4 }}>
         <Grid
           item
           textAlign='center'
           flexDirection='column'
           display='flex'
           alignItems='center'
           rowGap={theme.rowGap}
           xs={8}
         >
           <Box
             display='flex'
             justifyContent='center'
             alignItems='center'
             flexDirection='column'
           >
             <Typography sx={{ mb: '2rem' }} variant='body1'>
               {step.quiz.final && (
                 <CourseRating
                   course={course}
                   setRatingAdded={setRatingAdded}
                 />
               )}
             </Typography>
           </Box>
         </Grid>
       </Container>
     )
   }

    return (
      <Container maxWidth='sm' sx={{ alignItems: 'center', pt: 4 }}>
        {already_completed_before ? (
          <Box
            gap={4}
            display='flex'
            justifyContent='center'
            alignItems='center'
            flexDirection='column'
          >
            <CheckmarkCircle
              height='10rem'
              width='10rem'
              variant='success'
              checkSize='size-56'
            />
            <Typography sx={{ alignSelf: 'center' }} variant='h4'>
              {t('quiz_already_completed')}
            </Typography>
            {step.quiz.final && !step.course.hasRating && (
              <CourseRating course={course} setRatingAdded={setRatingAdded} />
            )}
          </Box>
        ) : (
          <Grid
            item
            textAlign='center'
            flexDirection='column'
            display='flex'
            alignItems='center'
            rowGap={theme.rowGap}
            xs={8}
          >
            <CheckmarkCircle
              height='10rem'
              width='10rem'
              checkSize='size-56'
              variant={step.quiz?.isPassed ? 'success' : 'failure'}
            />
            <Typography variant='h4'>
              {t('control_question_answers_feedback', {
                numCorrect,
                totalQuestions: step.quiz?.tasks.length,
              })}
            </Typography>
            {step.quiz?.isPassed ? (
              <Box
                display='flex'
                justifyContent='center'
                alignItems='center'
                flexDirection='column'
              >
                <Typography sx={{ mb: '2rem', mt: '2rem' }} variant='h5'>
                  {t('completed_quiz_result_feedback')}!
                </Typography>
                <Typography sx={{ mb: '2rem' }} variant='body1'>
                  {t('completed_quiz_result_details')}
                  {step.quiz.final && (
                    <CourseRating
                      course={course}
                      setRatingAdded={setRatingAdded}
                    />
                  )}
                </Typography>
              </Box>
            ) : (
              <Box display='flex' alignItems='center' flexDirection='column'>
                <Typography sx={{ mb: '2rem' }} variant='body1'>
                  {t('failed_quiz_result_details')}
                </Typography>
              </Box>
            )}
          </Grid>
        )}
      </Container>
    )
  })
)

